import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, filter, Observable, switchMap, takeWhile, tap, throwError, timer } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { TablePayload } from '../models/utils-table';

interface ExportRequestResult {
  id: string;
}

type Status = 'Pending' | 'Processing' | 'Completed' | 'Failed';

interface ExportStatus {
  status: Status;
  progressPercentage: number;
}

interface ExportPayload extends Partial<TablePayload> {
  entityIds: string[];
}

@Injectable({
  providedIn: 'root'
})
export class ExcelExportService {
  private exportProgressMap = new Map<number, BehaviorSubject<number>>();

  constructor(private http: HttpClient) {}

  exportExcel({ search, filters, tabId, sort }: Partial<TablePayload>, entityIds: string[]): Observable<Blob> {
    const retryInterval = 3000;
    const body: ExportPayload = {
      search: search || null,
      filters: filters || [],
      sort: sort || null,
      tabId,
      entityIds
    };
    const pendingStatuses: Partial<Status>[] = ['Pending', 'Processing'];
    const completedStatuses: Partial<Status>[] = ['Completed', 'Failed'];

    return this.http.post<ExportRequestResult>(`${environment.apiUrl}/exports`, body).pipe(
      switchMap(({ id }) =>
        timer(0, retryInterval).pipe(
          switchMap(() => this.http.get<ExportStatus>(`${environment.apiUrl}/exports/${id}`)),
          takeWhile(({ status }) => pendingStatuses.includes(status), true),
          tap(({ progressPercentage }) => this.updateExportProgressFor(tabId!, progressPercentage)),
          filter(({ status }) => completedStatuses.includes(status)),
          switchMap(({ status }) => {
            this.updateExportProgressFor(tabId!, 0);
            if (status === 'Failed') {
              return throwError(() => new Error('Export failed'));
            } else {
              return this.http.get(`${environment.apiUrl}/exports/${id}/file`, { responseType: 'blob' });
            }
          })
        )
      )
    );
  }

  getProgressFor(tabId: number) {
    return this.getOrCreateExportProgressFor(tabId).asObservable();
  }

  private getOrCreateExportProgressFor(tabId: number) {
    if (!this.exportProgressMap.has(tabId)) {
      this.exportProgressMap.set(tabId, new BehaviorSubject(0));
    }
    return this.exportProgressMap.get(tabId)!;
  }

  private updateExportProgressFor(tabId: number, progressPercentage: number) {
    this.getOrCreateExportProgressFor(tabId).next(progressPercentage);
  }
}
